// ACO Braintree payment integration
// Version date: 2025-07-09

import * as $ from "jquery";

declare var braintree: any;
declare var grecaptcha: any;

// assume these IDs for dom objects
const containerid = "#dropin-container";
const submitbuttonid = "#submit-button";
const processingid = "#processing-message";
const checkoutmessageid = "#checkout-message";

export interface BraintreeConfig {
    clientToken: string;
    urlProcess: string; // '/Registration/PaymentBTProcess.ashx'
    urlReceipt: string; // '/Registration/RegisterReceipt.aspx'
    recaptchaKey?: string; // optional, used for reCAPTCHA
}

export class BraintreePay {
    config: BraintreeConfig;
    submitButton: HTMLInputElement;
    container: HTMLDivElement;
    processing: HTMLDivElement;
    checkoutmessage: HTMLDivElement;

    constructor(config: BraintreeConfig) {
        this.config = config;
        this.submitButton = document.querySelector(
            submitbuttonid,
        ) as HTMLInputElement;
        this.container = document.querySelector(containerid) as HTMLDivElement;
        this.processing = document.querySelector(
            processingid,
        ) as HTMLDivElement;
        this.checkoutmessage = document.querySelector(
            checkoutmessageid,
        ) as HTMLDivElement;
    }

    initalize() {
        const self = this;
        braintree.dropin.create({
            // Insert your tokenization key here
            authorization: this.config.clientToken,
            container: "#dropin-container",
        }, function (createErr: string, instance) {
            if (createErr) {
                // Handle any errors that might've occurred when creating Drop-in
                console.error(createErr);
                self.showError(createErr);
                return;
            }
            self.submitButton.addEventListener("click", function () {
                self.handleSubmit(instance);
            });
            self.submitButton.disabled = false;
        });
    }

    handleSubmit(instance: any) {
        const self = this;
        self.submitButton.disabled = true;
        self.container.style.display = "none";
        self.processing.style.display = "block";
        instance.requestPaymentMethod(
            function (requestPaymentMethodErr: string | null, payload) {
                // When the user clicks on the 'Submit payment' button this code will send the
                // encrypted payment information in a variable called a payment method nonce
                if (requestPaymentMethodErr) {
                    console.error(requestPaymentMethodErr);
                    self.showError(requestPaymentMethodErr);
                    return;
                } else {
                    self.sendPaymentSuccess(instance, payload);
                }
            },
        );
    }

    async getRecaptchaToken(timeout: number = 2000): Promise<string> {
        // wrap grecaptcha.enterprise.ready in a Promise
        const readyPromise = new Promise<void>((resolve) => {
            grecaptcha.enterprise.ready(() => resolve());
        });

        // create a timeout Promise that rejects after `timeout` ms
        const timeoutPromise = new Promise<never>((_, reject) => {
            const id = setTimeout(() => {
                clearTimeout(id);
                reject(new Error(`reCAPTCHA ready timed out after ${timeout} ms`));
            }, timeout);
        });

        // wait for whichever comes first
        await Promise.race([readyPromise, timeoutPromise]);

        // once ready, execute and return the token
        return grecaptcha.enterprise.execute(
            this.config.recaptchaKey!,
            { action: 'pay' }
        );
    }

    async sendPaymentSuccess(instance: any, payload: any): Promise<void> {
        try {
            let recaptchaToken: string;
            if (this.config.recaptchaKey) {
                recaptchaToken = await this.getRecaptchaToken();
            }
            else {
                recaptchaToken = ''; // No reCAPTCHA key provided, use empty string
            }
            const response = await fetch(this.config.urlProcess, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                },
                body: new URLSearchParams({
                    payment_method_nonce: payload.nonce,
                    recaptcha_token: recaptchaToken,
                }),
            });

            if (!response.ok) {
                throw new Error(`Network response was not ok (${response.status})`);
            }

            const result = await response.json();

            if (result.success) {
                window.location.href = this.config.urlReceipt;
            } else {
                instance.clearSelectedPaymentMethod();
                this.showError(
                    `<strong>Payment Error</strong> ${result.message}`
                );
            }
        } catch (err: any) {
            console.error("Payment request failed:", err);
            this.showError(err.message || "An unexpected error occurred.");
        }
    }

    showError(errormsg: string) {
        this.submitButton.disabled = false;
        this.container.style.display = "block";
        this.processing.style.display = "none";
        if (errormsg) {
            this.checkoutmessage.style.display = "block";
            this.checkoutmessage.innerHTML = errormsg;
        }
    }
}
